import { Component, DestroyRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DatePipe, NgClass, NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { Offcanvas } from 'bootstrap';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';
import {
  ActivityLog,
  ChecklistItem,
  initials,
  overdueDays,
  relatedTypeLabel,
  Task,
  TASK_STATUSES,
  TaskComment,
  TaskHistory,
  taskPriorityClass,
  TaskStatus,
  taskStatusClass,
  TaskUpload,
} from '../task.model';
import { TaskFormComponent } from '../task-form/task-form.component';
import { LazyPicker, staffPicker } from '../lazy-picker';

type Tab = 'comments' | 'attachments' | 'history' | 'activity';

@Component({
  selector: 'app-task-view',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    NgIf,
    NgFor,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    CKEditorModule,
    DatePipe,
    TitleCasePipe,
    NgSelectModule,
    TaskFormComponent,
  ],
  templateUrl: './task-view.component.html',
  styleUrls: ['./task-view.component.css'],
})
export class TaskViewComponent implements OnInit, OnDestroy {
  @ViewChild('taskForm') taskForm!: TaskFormComponent;
  @ViewChild('holdReasonModal') holdReasonModal!: TemplateRef<any>;
  @ViewChild('reassignModal') reassignModal!: TemplateRef<any>;

  breadcrumbList = {
    breadcrumb_path: 'Tasks',
    currentURL: 'View Task',
    breadcrumb_url: '/admin/tasks',
  };

  taskId = '';
  task: Task | null = null;
  comments: TaskComment[] = [];
  history: TaskHistory[] = [];
  activity: ActivityLog[] = [];
  checklist: ChecklistItem[] = [];
  otherTasks: Task[] = [];
  activeTab: Tab = 'comments';
  commentForm!: FormGroup;
  public Editor: any = ClassicEditor;

  statuses = TASK_STATUSES;
  holdReason = '';
  reassignTo: string | null = null;
  assignees: LazyPicker;

  // Checklist editing (labels)
  editingChecklist = false;
  checklistDraft: { id?: string; label: string }[] = [];
  newChecklistLabel = '';

  canViewComments = false;
  canAddComments = false;
  canUpdate = false;
  canDelete = false;
  canAssign = false;

  statusClass = taskStatusClass;
  priorityClass = taskPriorityClass;
  initials = initials;
  overdueDays = overdueDays;
  relatedTypeLabel = relatedTypeLabel;

  private commentDrawer!: Offcanvas;
  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private httpService: HttpServiceService,
    public loadingService: LoadingService,
    private localStorageService: LocalStorageService,
    private fb: FormBuilder,
    private alertService: NgxToastrService,
    private modalService: NgbModal,
    private destroyRef: DestroyRef,
  ) {
    this.assignees = staffPicker(httpService, destroyRef);
    this.commentForm = this.fb.group({ comment: ['', Validators.required] });
  }

  ngOnInit(): void {
    this.checkPermissions();
    const paramSub = this.route.paramMap.subscribe((params) => {
      this.taskId = params.get('id') || '';
      if (this.taskId) this.loadAll();
    });
    this.subscriptions.add(paramSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.commentDrawer) this.commentDrawer.dispose();
  }

  checkPermissions(): void {
    const p = USER_PERMISSIONS.TASKS;
    this.canViewComments = this.localStorageService.checkPermission(p.TASKS_VIEW_COMMENTS);
    this.canAddComments = this.localStorageService.checkPermission(p.TASKS_ADD_COMMENTS);
    this.canUpdate = this.localStorageService.checkPermission(p.TASKS_UPDATE);
    this.canDelete = this.localStorageService.checkPermission(p.TASKS_DELETE);
    this.canAssign = this.localStorageService.checkPermission(p.TASKS_ASSIGN);
    if (!this.canViewComments) this.activeTab = 'attachments';
  }

  loadAll(): void {
    this.getTaskDetails();
    if (this.canViewComments) this.getComments();
    this.getHistory();
    this.getActivity();
    this.getChecklist();
  }

  // ── Loads ──────────────────────────────────────────────────────────────
  getTaskDetails(): void {
    this.loadingService.show();
    const sub = this.httpService.getTaskDetailsById(this.taskId).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.task = response.data;
          this.breadcrumbList = { ...this.breadcrumbList, currentURL: this.task?.task_id || 'View Task' };
          if (this.task?.property?.id) this.getOtherTasks(this.task.property.id);
          else this.otherTasks = [];
        }
      },
      error: () => this.loadingService.hide(),
    });
    this.subscriptions.add(sub);
  }

  getOtherTasks(propertyId: string): void {
    const sub = this.httpService
      .getAllTasks(1, 6, 'created_at', 'DESC', `&property_id=${propertyId}`)
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.otherTasks = (response.data.tasks ?? []).filter((t: Task) => t.id !== this.taskId).slice(0, 5);
          }
        },
        error: () => {},
      });
    this.subscriptions.add(sub);
  }

  getComments(): void {
    const sub = this.httpService.getTaskComments(this.taskId).subscribe({
      next: (response: any) => {
        if (response.success) this.comments = response.data;
      },
      error: () => {},
    });
    this.subscriptions.add(sub);
  }

  getHistory(): void {
    const sub = this.httpService.getTaskHistory(this.taskId).subscribe({
      next: (response: any) => {
        if (response.success) this.history = response.data;
      },
      error: () => {},
    });
    this.subscriptions.add(sub);
  }

  getActivity(): void {
    const sub = this.httpService.getTaskActivity(this.taskId).subscribe({
      next: (response: any) => {
        if (response.success) this.activity = response.data ?? [];
      },
      error: () => {},
    });
    this.subscriptions.add(sub);
  }

  getChecklist(): void {
    const sub = this.httpService.getTaskChecklist(this.taskId).subscribe({
      next: (response: any) => {
        if (response.success) this.checklist = response.data ?? [];
      },
      error: () => {},
    });
    this.subscriptions.add(sub);
  }

  // ── Derived ────────────────────────────────────────────────────────────
  get assignee() {
    return this.task?.assignee ?? this.task?.owner ?? null;
  }

  get checklistDone(): number {
    return this.checklist.filter((i) => i.is_done).length;
  }

  get sortedUploads(): TaskUpload[] {
    const uploads = this.task?.uploads ?? [];
    return [...uploads].sort((a, b) => (b.kind === 'completion_photo' ? 1 : 0) - (a.kind === 'completion_photo' ? 1 : 0));
  }

  get isDone(): boolean {
    return this.task?.status === TaskStatus.DONE;
  }

  isImage(u: TaskUpload): boolean {
    return (u.mime_type || '').startsWith('image/');
  }

  plannedWindow(): string {
    const t = this.task;
    if (!t) return '';
    if (t.planned_start && t.planned_end) return `${t.planned_start}–${t.planned_end}`;
    return t.planned_start || t.planned_end || '';
  }

  activityLabel(a: ActivityLog): string {
    if (!a.ended_at) return 'Checked in';
    return a.source === 'manual' ? 'Logged manually' : 'Checked out';
  }

  setActiveTab(tab: Tab): void {
    this.activeTab = tab;
  }

  // ── Actions ────────────────────────────────────────────────────────────
  edit(): void {
    this.taskForm.open(this.taskId);
  }

  onSaved(): void {
    this.loadAll();
  }

  changeStatus(status: string): void {
    if (!this.task || status === this.task.status) return;
    if (status === TaskStatus.ON_HOLD) {
      this.holdReason = '';
      this.modalService
        .open(this.holdReasonModal, { centered: true, backdrop: 'static' })
        .result.then((reason: string) => this.putStatus(status, (reason || '').trim()), () => {});
      return;
    }
    this.putStatus(status);
  }

  private putStatus(status: string, hold_reason?: string): void {
    if (status === TaskStatus.ON_HOLD && !hold_reason) return;
    this.loadingService.show();
    this.httpService.updateTaskStatus(this.taskId, hold_reason ? { status, hold_reason } : { status }).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success(response.message || 'Status updated', 'toast-top-right');
          this.loadAll();
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  openReassign(): void {
    this.reassignTo = this.assignee?.id ?? null;
    this.assignees.init();
    if (this.assignee) this.assignees.ensure({ value: this.assignee.id, label: this.assignee.name });
    this.modalService
      .open(this.reassignModal, { centered: true, backdrop: 'static' })
      .result.then((assignee_id: string) => {
        if (!assignee_id) return;
        this.loadingService.show();
        this.httpService.assignTask(this.taskId, { assignee_id }).subscribe({
          next: (response: any) => {
            this.loadingService.hide();
            if (response.success) {
              this.alertService.success(response.message || 'Task reassigned', 'toast-top-right');
              this.loadAll();
            }
          },
          error: () => this.loadingService.hide(),
        });
      }, () => {});
  }

  verify(): void {
    if (!this.isDone || !confirm('Mark this task as verified?')) return;
    this.loadingService.show();
    this.httpService.verifyTask(this.taskId).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success(response.message || 'Task verified', 'toast-top-right');
          this.loadAll();
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  deleteTask(): void {
    if (!confirm('Are you sure you want to delete this task?')) return;
    this.loadingService.show();
    this.httpService.deleteTask(this.taskId).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success(response.message, 'toast-top-right');
          this.router.navigate(['/admin/tasks']);
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  // ── Checklist ──────────────────────────────────────────────────────────
  toggleItem(item: ChecklistItem): void {
    if (!this.canUpdate) return;
    const next = !item.is_done;
    item.is_done = next;
    this.httpService.toggleTaskChecklistItem(this.taskId, item.id, next).subscribe({
      next: (response: any) => {
        if (response.success && response.data?.id) Object.assign(item, response.data);
        if (this.task?.checklist) this.task.checklist = { done: this.checklistDone, total: this.checklist.length };
      },
      error: () => (item.is_done = !next),
    });
  }

  startEditChecklist(): void {
    this.checklistDraft = this.checklist.map((i) => ({ id: i.id, label: i.label }));
    this.newChecklistLabel = '';
    this.editingChecklist = true;
  }

  addDraftItem(): void {
    const label = this.newChecklistLabel.trim();
    if (!label) return;
    this.checklistDraft.push({ label });
    this.newChecklistLabel = '';
  }

  removeDraftItem(i: number): void {
    this.checklistDraft.splice(i, 1);
  }

  cancelEditChecklist(): void {
    this.editingChecklist = false;
  }

  saveChecklist(): void {
    const items = this.checklistDraft.filter((i) => i.label.trim()).map((i) => ({ id: i.id, label: i.label.trim() }));
    this.loadingService.show();
    this.httpService.updateTaskChecklist(this.taskId, { items }).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.checklist = Array.isArray(response.data) ? response.data : this.checklist;
          this.editingChecklist = false;
          if (!Array.isArray(response.data)) this.getChecklist();
          this.alertService.success(response.message || 'Checklist updated', 'toast-top-right');
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  // ── Comments ───────────────────────────────────────────────────────────
  openCommentDrawer(): void {
    const el = document.getElementById('commentDrawer');
    if (el) {
      this.commentDrawer = Offcanvas.getOrCreateInstance(el);
      this.commentDrawer.show();
    }
  }

  closeCommentDrawer(): void {
    if (this.commentDrawer) {
      this.commentDrawer.hide();
      this.commentForm.reset();
    }
  }

  saveComment(): void {
    if (this.commentForm.invalid) return;
    this.loadingService.show();
    const sub = this.httpService.saveTaskComment(this.taskId, this.commentForm.value).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success(response.message || 'Comment added successfully', 'toast-top-right');
          this.closeCommentDrawer();
          this.getComments();
        }
      },
      error: () => this.loadingService.hide(),
    });
    this.subscriptions.add(sub);
  }

  backToTasks(): void {
    this.router.navigate(['/admin/tasks']);
  }
}
